import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { createPayment, updatePayment, getProjects } from '../services/api';
import { usePaymentStore } from '../stores/paymentStore';
import { useCustomerStore } from '../stores/customerStore';
import { Input, Label, FormField, FormError, Textarea, CustomSelect, LoadingButton, dialogButtonClass, DialogShell, DialogHeader, DialogFooter } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { notifyDataChanged } from '../utils/dataEvents';
import { useTranslation } from 'react-i18next';
import { getTodayDateString } from '../utils/dateUtils';
import { canEditArchivedCustomerData } from '../utils/customerUtils';

interface Project {
  id: string;
  name: string;
  customerId: string;
}

interface PaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  payment?: {
    id: string;
    customerId: string;
    projectId?: string;
    amount: number;
    paymentMethod: string;
    paymentDate: string;
    notes?: string;
    customer: {
      id: string;
      name: string;
      userId: string;
      archived?: boolean;
    };
    project?: Project;
  };
  preselectedCustomerId?: string;
}

interface FormData {
  customerId: string;
  projectId?: string;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  notes?: string;
}

// 專案下拉的上次清單（記憶體，重新整理後清空）：再開對話框時先顯示，背景更新
let projectOptionsCache: Project[] | null = null;

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  isOpen,
  onClose,
  payment,
  preselectedCustomerId,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm<FormData>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [projects, setProjects] = useState<Project[]>(projectOptionsCache ?? []);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(!projectOptionsCache);
  
  const { addPayment: addPaymentToStore, updatePayment: updatePaymentInStore } = usePaymentStore();
  const { allCustomers, isLoadingAllCustomers, fetchAllCustomers } = useCustomerStore();


  const isEditMode = !!payment;
  
  // 檢查是否可以編輯（封存客戶的關聯資料不能編輯）
  const canEdit = !isEditMode || canEditArchivedCustomerData(payment?.customer);
  
  // 監聽表單值變化
  const watchedCustomerId = watch('customerId', '');
  const watchedProjectId = watch('projectId', '');

  // 載入客戶和專案資料
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (payment) {
        // 編輯模式
        setValue('customerId', payment.customerId);
        setValue('projectId', payment.projectId || '');
        setValue('amount', payment.amount);
        setValue('paymentMethod', payment.paymentMethod);
        setValue('paymentDate', payment.paymentDate.split('T')[0]);
        setValue('notes', payment.notes || '');
      } else {
        // 新增模式
        reset();
        if (preselectedCustomerId) {
          setValue('customerId', preselectedCustomerId);
        }
        setValue('paymentMethod', t('common.forms.paymentMethods.bankTransfer'));
        // 設置預設付款日期為今天
        setValue('paymentDate', getTodayDateString());
      }
    }
  }, [isOpen, payment, preselectedCustomerId, setValue, reset]);

  // 客戶、專案下拉同時背景更新（原本先等客戶載完才抓專案）；專案有上次的清單就先顯示，不閃「載入中」
  const loadData = async () => {
    fetchAllCustomers();
    if (!projectOptionsCache) setProjectsLoading(true);
    try {
      // 下拉要有全部專案（預設分頁只有 20 筆，第 21 筆以後選不到）
      const projectsResponse = await getProjects(1, { pageSize: 1000 });
      projectOptionsCache = projectsResponse.data.data;
      setProjects(projectOptionsCache);
    } catch (error) {
      console.error('載入資料失敗:', error);
      if (!projectOptionsCache) toast.error(t('common.messages.fetchFailed'));
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitError(null);
    reset();
    onClose();
  };

  // 當客戶變更時，檢查目前選擇的專案是否還適用
  const handleCustomerChange = (customerId: string) => {
    setValue('customerId', customerId);
    
    const currentProjectId = watchedProjectId;
    if (currentProjectId) {
      const selectedProject = projects.find(project => project.id === currentProjectId);
      if (selectedProject && selectedProject.customerId !== customerId) {
        // 如果目前選的專案不屬於新選的客戶，清空專案選擇
        setValue('projectId', '');
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    // 檢查是否可以編輯封存客戶的資料
    if (isEditMode && !canEdit) {
      toast.error(t('errors.business.cannotEditArchivedCustomerData', '無法編輯封存客戶的關聯資料'));
      return;
    }
    
    setSubmitError(null);
    setLoading(true);

    try {
      const submitData = {
        customerId: data.customerId,
        projectId: data.projectId || null, // null＝取消關聯（undefined 會被 JSON 略掉，改不回空白）
        amount: Number(data.amount),
        paymentMethod: data.paymentMethod.trim(),
        paymentDate: data.paymentDate ? `${data.paymentDate}T00:00:00.000Z` : undefined,
        notes: data.notes?.trim(),
      };

      if (isEditMode) {
        // 財務操作：等待式更新（確保資料精確性）
        const response = await updatePayment(payment.id, submitData);
        updatePaymentInStore(payment.id, response.data);
        handleClose();
        toast.success(t('common.messages.updateSuccess'));
      } else {
        const response = await createPayment(submitData);
        addPaymentToStore(response.data);
        handleClose();
        toast.success(t('common.messages.createSuccess'));
      }

      notifyDataChanged('payments'); // 儀表板、動態、報價狀態（收款足額會變已完成）重新載入
    } catch (error: any) {
      console.error('付款操作失敗:', error);
      if (error.response?.data?.error) {
        setSubmitError(error.response.data.error);
      } else {
        setSubmitError(isEditMode ? t('common.messages.updateFailed') : t('common.messages.createFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // 篩選當前客戶的專案
  const filteredProjects = projects.filter(project => {
    if (!watchedCustomerId) return true;
    return project.customerId === watchedCustomerId || project.id === watchedProjectId;
  });

  return (
    <DialogShell onClose={handleClose} closeDisabled={loading} className="max-w-2xl">
      <DialogHeader title={isEditMode ? t('payments.edit.title', { ns: 'pages' }) : t('payments.add.title', { ns: 'pages' })} onClose={handleClose} closeDisabled={loading} closeLabel={t('common.actions.close')} />

      {/* 對話框內容 */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          {/* 封存客戶警告提示 */}
          {isEditMode && !canEdit && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('common.warnings.archivedCustomerData')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                    <p>{t('common.warnings.archivedCustomerDataDetail')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* 客戶選擇 */}
          <FormField>
            <Label htmlFor="customerId">{t('payments.form.customer', { ns: 'pages' })} *</Label>
            <CustomSelect
              id="customerId"
              {...register('customerId', { required: t('payments.form.selectCustomer', { ns: 'pages' }) })}
              value={watchedCustomerId}
              onChange={handleCustomerChange}
              variant={errors.customerId ? "error" : "default"}
              disabled={isLoadingAllCustomers}
              placeholder={t('common.forms.placeholders.selectCustomer')}
              options={[
                ...(isLoadingAllCustomers ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...allCustomers
                  .filter(customer => customer.archived !== true)
                  .sort((a, b) => new Date(b.createdAt || b.updatedAt || '').getTime() - new Date(a.createdAt || a.updatedAt || '').getTime())
                  .map(customer => ({ 
                    value: customer.id, 
                    label: `${customer.name}${customer.company ? ` (${customer.company})` : ''}` 
                  }))
              ]}
            />
            {errors.customerId && <FormError>{errors.customerId.message}</FormError>}
          </FormField>

          {/* 關聯專案 */}
          <FormField>
            <Label htmlFor="projectId">{t('payments.form.project', { ns: 'pages' })}</Label>
            <CustomSelect
              id="projectId"
              {...register('projectId')}
              value={watchedProjectId}
              onChange={(value) => setValue('projectId', value)}
              variant={errors.projectId ? "error" : "default"}
              disabled={projectsLoading || !watchedCustomerId}
              placeholder={t('common.forms.placeholders.selectProject')}
              options={[
                ...(projectsLoading ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...(!watchedCustomerId ? [{ value: 'no-customer', label: t('payments.form.selectCustomerFirst', { ns: 'pages' }), disabled: true }] : []),
                ...(watchedCustomerId && filteredProjects.length === 0 ? 
                  [{ value: 'no-projects', label: t('payments.form.noProjects', { ns: 'pages' }), disabled: true }] : []),
                ...filteredProjects.map(project => ({ 
                  value: project.id, 
                  label: project.name 
                }))
              ]}
            />
            {errors.projectId && <FormError>{errors.projectId.message}</FormError>}
          </FormField>

          {/* 付款金額 */}
          <FormField>
            <Label htmlFor="amount">{t('payments.form.amount', { ns: 'pages' })} *</Label>
            <Input
              type="text"
              id="amount"
              {...register('amount', { 
                required: t('payments.form.amount', { ns: 'pages' }),
                pattern: {
                  value: /^\d+(\.\d{0,2})?$/,
                  message: t('common.forms.validation.invalidAmount')
                }
              })}
              variant={errors.amount ? "error" : "default"}
              placeholder="0"
            />
            {errors.amount && <FormError>{errors.amount.message}</FormError>}
          </FormField>

          {/* 付款方式 */}
          <FormField>
            <Label htmlFor="paymentMethod">{t('payments.form.paymentMethod', { ns: 'pages' })} *</Label>
            <Input
              type="text"
              id="paymentMethod"
              {...register('paymentMethod', { 
                required: t('payments.form.paymentMethod', { ns: 'pages' }),
                maxLength: { value: 100, message: t('common.forms.validation.maxLength', { length: 100 }) }
              })}
              variant={errors.paymentMethod ? "error" : "default"}
            />
            {errors.paymentMethod && <FormError>{errors.paymentMethod.message}</FormError>}
          </FormField>

          {/* 付款日期 */}
          <FormField>
            <Label htmlFor="paymentDate">{t('payments.form.paymentDate', { ns: 'pages' })} *</Label>
            <div className="relative">
              <Input
                type="date"
                id="paymentDate"
                {...register('paymentDate', { required: t('payments.form.paymentDate', { ns: 'pages' }) })}
                variant={errors.paymentDate ? "error" : "default"}
                className="cursor-pointer"
                onMouseDown={(e) => {
                  // 防止文字被選取
                  e.preventDefault();
                }}
                onClick={(e) => {
                  // 為整個輸入欄位加入點擊事件，開啟日期選擇器
                  const input = e.target as HTMLInputElement;
                  if (input.type === 'date' && input.showPicker) {
                    try {
                      input.showPicker();
                    } catch (error) {
                      console.log('showPicker not supported or failed');
                    }
                  }
                }}
              />
            </div>
            {errors.paymentDate && <FormError>{errors.paymentDate.message}</FormError>}
          </FormField>

          {/* 備註 */}
          <FormField>
            <Label htmlFor="notes">{t('payments.form.notes', { ns: 'pages' })}</Label>
            <Textarea
              id="notes"
              {...register('notes', {
                maxLength: { value: 1000, message: t('common.forms.validation.maxLength', { length: 1000 }) }
              })}
              rows={3}
              variant={errors.notes ? "error" : "default"}
            />
            {errors.notes && <FormError>{errors.notes.message}</FormError>}
          </FormField>

          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={handleClose}
            className={dialogButtonClass.cancel}
            disabled={loading}
          >
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            loading={loading}
            isEditMode={isEditMode}
            disabled={isLoadingAllCustomers || projectsLoading || !canEdit}
            t={t}
          />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default PaymentDialog;